import { Link } from "react-router-dom";
import { Loader2, FolderOpen, CheckCircle2, Bell, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export function PageLoading({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center py-24 text-slate-400" data-testid="page-loading">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> {label}…
    </div>
  );
}

export function SkeletonRows({ rows = 5 }) {
  return (
    <div className="space-y-3" data-testid="skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton h-14 rounded-xl" />
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon = FolderOpen, title, subtitle, action, testid }) {
  return (
    <div className="card-soft flex flex-col items-center justify-center px-6 py-16 text-center" data-testid={testid || "empty-state"}>
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50 text-slate-300">
        <Icon className="h-8 w-8" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-xl font-bold text-navy">{title}</h3>
      {subtitle && <p className="mt-2 max-w-sm text-sm text-slate-500">{subtitle}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function StatCard({ label, value, accent = "navy", hint, icon: Icon, testid }) {
  const tones = {
    navy: "text-navy",
    cyan: "text-cyan-dark",
    green: "text-emerald-600",
    amber: "text-amber-600",
    red: "text-red-600",
    slate: "text-slate-500",
  };
  return (
    <div className="card-soft p-5 transition-transform hover:-translate-y-0.5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <span className="overline text-slate-400">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${tones[accent]}`} strokeWidth={1.75} />}
      </div>
      <div className={`mt-3 font-heading text-3xl font-extrabold ${tones[accent]}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

export function PageHeader({ title, subtitle, action, icon: Icon }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-navy sm:text-3xl">
          {Icon && <Icon className="h-6 w-6 text-cyan-dark" strokeWidth={1.75} />}
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({ title = "Etwas ist schiefgelaufen", subtitle, onRetry, testid }) {
  return (
    <div className="card-soft flex flex-col items-center justify-center px-6 py-16 text-center" data-testid={testid || "error-state"}>
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 text-red-400">
        <AlertTriangle className="h-8 w-8" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-xl font-bold text-navy">{title}</h3>
      {subtitle && <p className="mt-2 max-w-sm text-sm text-slate-500">{subtitle}</p>}
      {onRetry && <div className="mt-6"><Button onClick={onRetry} variant="outline" data-testid="error-retry">Erneut versuchen</Button></div>}
    </div>
  );
}

export { CheckCircle2, Bell };
